import { Button } from '@awj/ui';

import { Icon } from '~/components/Icon.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { newKey, type OptionDraft, type ProductForm } from '~/lib/product-form.ts';

/**
 * The options a product is sold in — Size, Grind, and their values.
 *
 * Editing these is what drives the variant matrix below, so every change here is a change to
 * what the shop can sell. Removing an option is therefore explicit and never automatic: the
 * matrix is rebuilt on demand rather than silently on every keystroke, so nobody loses a column
 * of stock counts because they were mid-way through renaming something.
 */
export function OptionsEditor({
  form,
  onChange,
}: {
  form: ProductForm;
  onChange: (next: ProductForm) => void;
}) {
  const { t } = useI18n();

  function setOptions(options: OptionDraft[]) {
    onChange({ ...form, options });
  }

  function patch(index: number, changes: Partial<OptionDraft>) {
    setOptions(form.options.map((option, i) => (i === index ? { ...option, ...changes } : option)));
  }

  return (
    <section className="card" aria-labelledby="options-title">
      <div className="card__head">
        <h2 className="section-label" id="options-title">
          {t('catalog.options')}
        </h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            setOptions([
              ...form.options,
              { key: newKey('opt'), nameAr: '', nameEn: '', values: [] },
            ])
          }
        >
          {t('catalog.addOption')}
        </Button>
      </div>

      {form.options.length === 0 ? (
        <p className="field__hint">{t('catalog.noOptionsHint')}</p>
      ) : (
        <ul className="options">
          {form.options.map((option, index) => (
            <li className="option" key={option.key}>
              <div className="option__head">
                <div className="option__names">
                  <input
                    className="awj-input"
                    lang="ar"
                    dir="rtl"
                    value={option.nameAr}
                    placeholder={t('catalog.optionNameAr')}
                    aria-label={`${t('catalog.optionName')} — ${t('lang.arabic')}`}
                    onChange={(event) => patch(index, { nameAr: event.target.value })}
                  />
                  <input
                    className="awj-input"
                    lang="en"
                    dir="ltr"
                    value={option.nameEn}
                    placeholder={t('catalog.optionNameEn')}
                    aria-label={`${t('catalog.optionName')} — ${t('lang.english')}`}
                    onChange={(event) => patch(index, { nameEn: event.target.value })}
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOptions(form.options.filter((_, i) => i !== index))}
                >
                  {t('common.remove')}
                </Button>
              </div>

              <ul className="option__values">
                {option.values.map((value, valueIndex) => (
                  <li key={value.key}>
                    <input
                      className="awj-input"
                      lang="ar"
                      dir="rtl"
                      value={value.valueAr}
                      placeholder={t('catalog.valueAr')}
                      aria-label={`${t('catalog.optionValue')} — ${t('lang.arabic')}`}
                      onChange={(event) =>
                        patch(index, {
                          values: option.values.map((v, i) =>
                            i === valueIndex ? { ...v, valueAr: event.target.value } : v,
                          ),
                        })
                      }
                    />
                    <input
                      className="awj-input"
                      lang="en"
                      dir="ltr"
                      value={value.valueEn}
                      placeholder={t('catalog.valueEn')}
                      aria-label={`${t('catalog.optionValue')} — ${t('lang.english')}`}
                      onChange={(event) =>
                        patch(index, {
                          values: option.values.map((v, i) =>
                            i === valueIndex ? { ...v, valueEn: event.target.value } : v,
                          ),
                        })
                      }
                    />
                    {/*
                      A glyph, not the word. Every value in the list carried "Remove" spelled out,
                      which put a column of word-shaped controls down the edge of a list whose
                      actual content is two short boxes.
                    */}
                    <button
                      type="button"
                      className="icon-button icon-button--danger"
                      aria-label={t('common.remove')}
                      title={t('common.remove')}
                      onClick={() =>
                        patch(index, { values: option.values.filter((_, i) => i !== valueIndex) })
                      }
                    >
                      <Icon name="trash" size={17} />
                    </button>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="option__add"
                onClick={() =>
                  patch(index, {
                    values: [...option.values, { key: newKey('val'), valueAr: '', valueEn: '' }],
                  })
                }
              >
                <Icon name="plus" size={16} />
                {t('catalog.addValue')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
